import { getReadableColor } from 'helpers/getContrast'
import styled, { css } from 'styled-components'

const getMinHeight = (height, lineHeight) => {
    const minHeight = {
        small: 'auto',
        normal: `calc(${lineHeight} * 1 + 16px + 16px)`, // 1 lines + vertical padding
        large: `calc(${lineHeight} * 5 + 16px + 16px)`, // 5 lines + vertical padding
    }
    return minHeight[height]
}

export default styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${(p) => p.theme.editor.bg};
    border: ${(p) =>
        p.$borderStyle === 'rounded' && `1px solid ${p.theme.color.grey[40]}`};
    border-radius: ${(p) =>
        p.$borderStyle === 'rounded' && `${p.theme.radius.small}`};
    border-color: ${(p) => p.$hasError && p.theme.color.warn.main};
    position: relative; // Used to position the keyboard shortcuts button

    .TiptapEditorToolbar {
        background-color: ${(p) => p.theme.editor.bg};
        border-bottom: 1px solid ${(p) => p.theme.color.grey[30]};

        &[data-sticky='true'] {
            box-shadow: ${(p) => p.theme.shadow.soft.bottom};
            z-index: 9;
        }

        &:not([data-sticky='true']) {
            border-top-left-radius: ${(p) => p.theme.radius.small};
            border-top-right-radius: ${(p) => p.theme.radius.small};
        }
    }

    .TiptapEditorTools {
        display: flex;
        flex-wrap: wrap;
        max-width: ${(p) => p.theme.containerWidth.normal};
        margin-left: auto;
        margin-right: auto;
    }

    .TiptapEditorContent {
        flex-grow: 1;
    }

    .TiptapEditorToolGroup {
        flex-shrink: 0;
        position: relative;
        margin-bottom: -1px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        border-bottom: 1px solid ${(p) => p.theme.color.grey[30]};

        &:not(:last-child) {
            padding-right: 1px;

            &:after {
                content: '';
                position: absolute;
                top: 8px;
                bottom: 8px;
                right: 0;
                width: 1px;
                background-color: ${(p) => p.theme.color.grey[30]};
                pointer-events: none;
            }
        }
    }

    .is-editor-empty:first-child:before {
        content: attr(data-placeholder);
        float: left;
        color: ${(p) => p.theme.color.text.grey};
        pointer-events: none;
        height: 0;
    }

    .ProseMirror-hideselection *::selection {
        background: transparent;
    }
    .ProseMirror-hideselection *::-moz-selection {
        background: transparent;
    }
    .ProseMirror-hideselection * {
        caret-color: transparent;
    }

    .ProseMirror-gapcursor {
        display: none;
        position: absolute;
        pointer-events: none;
        margin-top: 0 !important;
    }

    .ProseMirror-gapcursor:after {
        content: '';
        display: block;
        position: absolute;
        top: 6px;
        height: 1px;
        width: 20px;
        background: black;
        animation: ProseMirror-cursor-blink 1.1s steps(2, start) infinite;
    }

    @keyframes ProseMirror-cursor-blink {
        to {
            visibility: hidden;
        }
    }

    .ProseMirror-focused .ProseMirror-gapcursor {
        display: block;

        + .focus {
            box-shadow: none;
        }
    }

    .TiptapEditorContentWrapper {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        position: relative; // Used to position messages like "Share update" in the editor's textarea
        transition: padding-top ${(p) => p.theme.transition.materialFast};

        ${(p) =>
            p.$labelIsActive &&
            css`
                padding-top: ${(p) => p.theme.font.lineHeight[p.$textSize]};
            `};
    }

    [lang] {
        border-bottom: 1px dashed ${(p) => p.theme.color.text.grey};
    }

    .mark-cssclass {
        ${(p) =>
            p.$allowTextCssClasses &&
            css`
                border-bottom: 1px dashed ${(p) => p.theme.color.text.grey};
            `};
    }

    .TiptapEditorLabel {
        font-size: ${(p) => p.theme.font.size[p.$textSize]};
        line-height: ${(p) => p.theme.font.lineHeight[p.$textSize]};
        top: ${(p) => p.theme.padding.vertical.small};
        height: ${(p) => p.theme.font.lineHeight[p.$textSize]};
        padding-left: 18px;

        ${(p) =>
            p.$labelIsActive &&
            css`
                transform: translateY(-2px);
            `};
    }

    .ProseMirror {
        width: 100%;
        height: 100%;
        min-height: ${(p) =>
            getMinHeight(p.$height, p.theme.font.lineHeight[p.$textSize])};
        margin-left: auto;
        margin-right: auto;
        padding: ${(p) =>
            `${p.theme.padding.vertical.small} ${p.theme.padding.horizontal.small} 38px ${p.theme.padding.horizontal.small}`};

        ${(p) =>
            p.$showPaddingBottom &&
            css`
                padding-bottom: 42px;
            `};

        ${(p) =>
            p.$textSize === 'small' &&
            css`
                font-size: ${(p) => p.theme.font.size.small};
                line-height: ${(p) => p.theme.font.lineHeight.small};
            `};
        ${(p) =>
            p.$textSize === 'normal' &&
            css`
                font-size: ${(p) => p.theme.font.size.normal};
                line-height: ${(p) => p.theme.font.lineHeight.normal};
            `};
        ${(p) =>
            p.$textSize === 'large' &&
            css`
                font-size: ${(p) => p.theme.font.size.large};
                line-height: ${(p) => p.theme.font.lineHeight.large};
            `};

        a {
            cursor: text;
        }

        .tiptap-details-summary-input {
            width: 100%;
            height: 40px;
            padding: 0;
            border: none;
            background: none;
            font-weight: ${(p) => p.theme.font.weight.bold};
        }

        .selectedCell:after {
            content: '';
            left: -2px;
            right: -2px;
            top: -2px;
            bottom: -2px;
            border: 2px solid black;
            pointer-events: none;
            position: absolute;
            z-index: 2;
        }

        .column-resize-handle {
            position: absolute;
            top: 0;
            bottom: 0;
            right: -3px;
            width: 4px;
            background-color: black;
            pointer-events: none;
        }

        &.ProseMirror-hideselection
            .node-mention.focus
            [data-node-view-wrapper]:before {
            border-radius: 1px;
            border: 2px solid black;
        }

        // Only in edit mode we show the hash-icon before the anchor link (beheer#13967)
        .mark-anchor > a::before {
            content: '#';
            color: ${(p) => p.theme.color.text.grey};
            margin-right: 1px;
        }
    }

    .resize-cursor {
        cursor: ew-resize;
        cursor: col-resize;
    }

    ${(p) =>
        p.$isCollab &&
        css`
            .collaboration-cursor__caret {
                position: absolute;
                border-bottom-color: transparent;
                border-color: transparent !important;
                pointer-events: none;
                user-select: none;
                z-index: 10; // on top of toolbar

                ${(p) =>
                    p.$textSize === 'small' &&
                    css`
                        border-bottom: ${(p) => p.theme.font.lineHeight.small}
                            solid transparent;
                    `};
                ${(p) =>
                    p.$textSize === 'normal' &&
                    css`
                        border-bottom: ${(p) => p.theme.font.lineHeight.normal}
                            solid transparent;
                    `};
                ${(p) =>
                    p.$textSize === 'large' &&
                    css`
                        border-bottom: ${(p) => p.theme.font.lineHeight.large}
                            solid transparent;
                    `};
            }

            .collaboration-cursor__label {
                position: relative;
                margin-left: -1px;
                background-color: ${(p) =>
                    p.theme.color.secondary.main} !important;
                color: ${(p) => getReadableColor(p.theme.color.secondary.main)};
                font-size: ${(p) => p.theme.font.size.tiny};
                line-height: ${(p) => p.theme.font.lineHeight.tiny};
                font-weight: ${(p) => p.theme.font.weight.semibold};
                padding: 0 3px 2px;
                border-radius: 2px 2px 2px 0;
                transform: translateY(-100%);

                &:before {
                    content: '';
                    display: block;
                    width: 2px;
                    position: absolute;
                    left: 0;
                    background-color: inherit;

                    ${(p) =>
                        p.$textSize === 'small' &&
                        css`
                            height: ${(p) => p.theme.font.lineHeight.small};
                            bottom: -${(p) => p.theme.font.lineHeight.small};
                        `};
                    ${(p) =>
                        p.$textSize === 'normal' &&
                        css`
                            height: ${(p) => p.theme.font.lineHeight.normal};
                            bottom: -${(p) => p.theme.font.lineHeight.normal};
                        `};
                    ${(p) =>
                        p.$textSize === 'large' &&
                        css`
                            height: ${(p) => p.theme.font.lineHeight.large};
                            bottom: -${(p) => p.theme.font.lineHeight.large};
                        `};
                }
            }
        `};

    ${({ $isFullScreen }) =>
        $isFullScreen &&
        css`
            position: fixed;
            inset: 0;
            width: 100vw;
            height: 100vh;
            z-index: 10;
            background-color: white;

            .TiptapEditorToolbar {
                top: 0 !important;
            }

            .TiptapEditorContentWrapper {
                width: 100%;
                max-width: 740px;
                margin: 0 auto;
                flex: 1;
                overflow: auto;
            }
        `}
`
